/**
 * The My brand screen's two records - `/api/v1/brand/` and
 * `/api/v1/payment-configuration/` - read on open and written on save.
 *
 * The screen is one form but two records, so both loading and saving it are
 * two calls. They are independent - neither reads the other, and the backend
 * applies each on its own - which is why the page fires them together and
 * reports them apart: a payment endpoint that is down is no reason to lose the
 * address that saved, or to leave the brand half of the form empty.
 *
 * `GET` and `PATCH` answer with the same body, so one mapping serves both.
 *
 * Files travel as ids. The bytes went up ahead of the save, through
 * `fileService`, so what these send is the `public_id` it answered with -
 * or `null`, which is how a removed logo or QR is cleared.
 *
 * Each function returns the fields of `BrandDetails` its own record owns,
 * built from the response rather than from what was posted: the backend
 * normalises as it stores (`+91` on the numbers, an upper-cased IFSC, a
 * lower-cased email and UPI address), and the form has to show what was
 * actually kept. The `details` passed in is only consulted for what the wire
 * cannot carry - the local preview of a file, and the WhatsApp checkbox, which
 * is a state of the form rather than a field of the record.
 */

import { ApiError, api, API_ENDPOINTS } from "../../../lib/api";
import { toNationalNumber } from "../../../lib/fieldFormat";
import type { GetToken } from "../../auth/types/auth.types";
import { mediaUrl, toStoredFile } from "./fileService";
import type {
  BrandDetails,
  BrandProfileRequest,
  BrandProfileResponse,
  PaymentConfigurationRequest,
  PaymentConfigurationResponse,
  StoredFileResponse,
  UploadedFile,
} from "../types/brand.types";

/**
 * The fields each record owns, which is also how a save is applied: the two
 * halves cover `BrandDetails` between them and do not overlap, so one landing
 * without the other leaves a form that is half committed rather than muddled.
 */
export const PROFILE_FIELDS = [
  "fullName",
  "brandName",
  "logo",
  "phone",
  "whatsapp",
  "whatsappSameAsPhone",
  "email",
  "address",
  "state",
  "city",
  "pincode",
] as const;

export const PAYMENT_FIELDS = [
  "accountHolder",
  "accountNumber",
  "bankName",
  "ifsc",
  "upiNumber",
  "upiId",
  "paymentQr",
] as const;

/** The brand half of the form, as the backend now holds it. */
export type ProfileDetails = Pick<
  BrandDetails,
  (typeof PROFILE_FIELDS)[number]
>;

/** The payment half of the form, as the backend now holds it. */
export type PaymentDetails = Pick<
  BrandDetails,
  (typeof PAYMENT_FIELDS)[number]
>;

/**
 * What the row listing a file should call it.
 *
 * The name it was uploaded under where the record kept one, which is the name
 * the dietitian picked the file by. Only a record stored before the backend
 * began keeping that falls back to the last segment of the URL - a stored id
 * with an extension on it, which names the file to nobody.
 */
const fileNameFrom = (file: StoredFileResponse): string =>
  file.original_name?.trim() ||
  decodeURIComponent(file.url.split("/").pop() || "file");

/**
 * A file in the response, kept in the shape the form holds files in.
 *
 * The local preview is reused where the response names the same file, which is
 * the ordinary case: the bytes are already in hand, and re-fetching them from
 * the API's origin would be a request for something the browser has. Only a
 * file this browser did not upload - the record saved from another device -
 * falls back to the stored URL.
 */
const toUploadedFile = (
  response: StoredFileResponse | null,
  current: UploadedFile | null
): UploadedFile | null => {
  if (!response) return null;

  if (current && current.publicId === response.public_id) return current;

  const stored = toStoredFile(response);
  return {
    name: fileNameFrom(response),
    dataUrl: mediaUrl(stored.url),
    publicId: stored.publicId,
    url: stored.url,
  };
};

/**
 * The brand half of the form, out of the record either verb answers with.
 *
 * `current` is the file the form already holds, if any - it is what lets an
 * upload keep the preview it read locally instead of fetching the same bytes
 * back. On a load there is nothing to keep, so it is `null`.
 */
const toProfileDetails = (
  saved: BrandProfileResponse["brand_profile"],
  current: UploadedFile | null
): ProfileDetails => {
  const phone = toNationalNumber(saved.phone_number ?? "");
  const whatsapp = toNationalNumber(saved.whatsapp_number ?? "");

  return {
    fullName: saved.contact_name ?? "",
    brandName: saved.brand_name ?? "",
    logo: toUploadedFile(saved.logo, current),
    phone,
    whatsapp,
    // The record does not hold the checkbox, only the two numbers - so on the
    // way in it is read back off them. Stored equal, they were linked.
    whatsappSameAsPhone: whatsapp !== "" && whatsapp === phone,
    email: saved.email ?? "",
    address: saved.address ?? "",
    state: saved.state ?? "",
    city: saved.city ?? "",
    pincode: saved.postal_code ?? "",
  };
};

/** The payment half, likewise. */
const toPaymentDetails = (
  saved: PaymentConfigurationResponse["payment_configuration"],
  current: UploadedFile | null
): PaymentDetails => ({
  accountHolder: saved.account_holder_name ?? "",
  accountNumber: saved.account_number ?? "",
  bankName: saved.bank_name ?? "",
  ifsc: saved.ifsc ?? "",
  upiNumber: toNationalNumber(saved.upi_number ?? ""),
  upiId: saved.upi_id ?? "",
  paymentQr: toUploadedFile(saved.payment_qr, current),
});

const toProfileRequest = (details: BrandDetails): BrandProfileRequest => ({
  brand_name: details.brandName,
  contact_name: details.fullName,
  phone_number: details.phone,
  whatsapp_number: details.whatsapp,
  email: details.email,
  address: details.address,
  city: details.city,
  state: details.state,
  postal_code: details.pincode,
  logo: details.logo?.publicId ?? null,
});

const toPaymentRequest = (
  details: BrandDetails
): PaymentConfigurationRequest => ({
  account_holder_name: details.accountHolder,
  account_number: details.accountNumber,
  bank_name: details.bankName,
  ifsc: details.ifsc,
  upi_number: details.upiNumber,
  upi_id: details.upiId,
  payment_qr: details.paymentQr?.publicId ?? null,
});

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot save ${what}.`);
  }
  return value;
};

/**
 * A record that has never been filled in is not an error.
 *
 * A workspace whose owner has not been to this screen yet has nothing under
 * either endpoint, and the answer to that is an empty form - so a 404, or a
 * body with the record missing, comes back as `null` rather than throwing.
 * Every other status still does.
 */
const unlessUnset = async <T>(read: Promise<T>): Promise<T | null> => {
  try {
    return await read;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

/** The brand half as it stands, or `null` where none has been saved yet. */
export async function fetchBrandProfile(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<ProfileDetails | null> {
  const response = await unlessUnset(
    api.get<BrandProfileResponse>(API_ENDPOINTS.brand.profile, {
      token: await token(getToken, "your brand details"),
      signal,
    })
  );

  // Nothing uploaded in this browser yet, so a stored logo arrives as its URL.
  return response?.brand_profile
    ? toProfileDetails(response.brand_profile, null)
    : null;
}

/** The payment half as it stands, or `null` where none has been saved yet. */
export async function fetchPaymentConfiguration(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<PaymentDetails | null> {
  const response = await unlessUnset(
    api.get<PaymentConfigurationResponse>(
      API_ENDPOINTS.brand.paymentConfiguration,
      { token: await token(getToken, "your payment details"), signal }
    )
  );

  return response?.payment_configuration
    ? toPaymentDetails(response.payment_configuration, null)
    : null;
}

/** Saves the brand half: name, contact details, address and logo. */
export async function saveBrandProfile(
  getToken: GetToken,
  details: BrandDetails,
  signal?: AbortSignal
): Promise<ProfileDetails> {
  const response = await api.patch<BrandProfileResponse>(
    API_ENDPOINTS.brand.profile,
    toProfileRequest(details),
    { token: await token(getToken, "your brand details"), signal }
  );

  return {
    ...toProfileDetails(response.brand_profile, details.logo),
    // Read off the numbers on a load, but not here: the box is how the form is
    // being filled in, and a save must not untick it under the cursor.
    whatsappSameAsPhone: details.whatsappSameAsPhone,
  };
}

/** Saves the payment half: bank details, UPI details and the QR. */
export async function savePaymentConfiguration(
  getToken: GetToken,
  details: BrandDetails,
  signal?: AbortSignal
): Promise<PaymentDetails> {
  const response = await api.patch<PaymentConfigurationResponse>(
    API_ENDPOINTS.brand.paymentConfiguration,
    toPaymentRequest(details),
    { token: await token(getToken, "your payment details"), signal }
  );

  return toPaymentDetails(response.payment_configuration, details.paymentQr);
}
